import { Navigate, Route, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';

import CandidateSourceOptionsDrawer from '../components/CandidateSourceOptionsDrawer';
import IsStaffChecker from '../components/IsStaffChecker';
import PermissionChecker from '../components/PermissionChecker';
import ApplicantAnalysis from '../pages/ApplicantAnalysis';
import CandidateAttachment from '../pages/ApplicantAnalysis/CandidateAttachment';
import CandidateQuestionnaire from '../pages/ApplicantAnalysis/CandidateQuestionnaire';
import ApplicantDetails from '../pages/ApplicantDetails';
import ApplicantHistory from '../pages/ApplicantHistory';
import CandidateCustomTests from '../pages/CandidateCustomTests';
import CandidateInterview from '../pages/CandidateInterview';
import CandidateInterviewOpinion from '../pages/CandidateInterviewOpinion';
import CandidateTests from '../pages/CandidateTests';
import AccessProfiles from '../pages/Configurations/AccessProfiles';
import CandidateRegisterFields from '../pages/Configurations/CandidateRegisterFields';
import CandidateTags from '../pages/Configurations/CandidateTags';
import CompanyInfo from '../pages/Configurations/CompanyInfo';
import EditInterviewScriptTemplate from '../pages/Configurations/EditInterviewScriptTemplate';
import EmailTemplates from '../pages/Configurations/EmailTemplates';
import Integrations from '../pages/Configurations/Integrations';
import InterviewScriptTemplates from '../pages/Configurations/InterviewScriptTemplates';
import RequestJobPostingTemplates from '../pages/Configurations/RequestJobPostingTemplates';
import EditMindsightKeyPair from '../pages/Configurations/StaffPages/EditMindsightKeyPair';
import ImportFiles from '../pages/Configurations/StaffPages/ImportFiles';
import MindsightKeyPairs from '../pages/Configurations/StaffPages/MindsightKeyPairs';
import StaffExternalAPI from '../pages/Configurations/StaffPages/StaffExternalAPI';
import StaffSankhyaConfiguration from '../pages/Configurations/StaffPages/StaffSankhyaConfiguration';
import StaffUsers from '../pages/Configurations/StaffPages/StaffUsers';
import StaffVixtingConfiguration from '../pages/Configurations/StaffPages/StaffVixtingConfiguration';
import TenantConfiguration from '../pages/Configurations/StaffPages/TenantConfiguration';
import Users from '../pages/Configurations/Users';
import VixtingAdmissionSettings from '../pages/Configurations/VixtingSettings';
import CreateAccessProfile from '../pages/CreateAccessProfile';
import CreateJobPosting from '../pages/CreateJobPosting';
import CreateOAuthClient from '../pages/CreateOAuthClient';
import CreateRequestJobPosting from '../pages/CreateRequestJobPosting';
import CreateUser from '../pages/CreateUser';
import CreateStaffUser from '../pages/CreateUser/CreateStaffUser';
import EditAccessProfile from '../pages/EditAccessProfile';
import { EditCandidateAttachmentPage, EditCandidateDetailsPage } from '../pages/EditCandidate';
import EditCandidateInterviewPage from '../pages/EditCandidate/EditCandidateInterviewPage';
import { EditEmailTemplatePage } from '../pages/EditEmailTemplate';
import * as EditOpportunity from '../pages/EditOpportunity';
import EditRequestJobPosting from '../pages/EditRequestJobPosting';
import { EditRequestJobPostingTemplatePage } from '../pages/EditRequestJobPostingTemplate';
import EditUser from '../pages/EditUser';
import EditStaffUser from '../pages/EditUser/EditStaffUser';
import ManagerDashboard from '../pages/ManagerDashboard';
import NotFound from '../pages/NotFound';
import Opportunities from '../pages/Opportunities';
import OpportunityDetails from '../pages/OpportunityDetails';
import Reports from '../pages/Reports';
import RequestJobPostingAnalysis from '../pages/RequestJobPostingAnalysis';
import RequestJobPostingHistory from '../pages/RequestJobPostingHistory';
import RequestJobPostings from '../pages/RequestJobPostings';
import ShowCandidateInterviews from '../pages/ShowCandidateInterviews';
import TalentPool from '../pages/TalentPool';
import VixtingAdmissionLogs from '../pages/VixtingAdmissionLogs';
import AccessManagementNavigator from './AccessManagementNavigator';
import ApplicantNavigator from './ApplicantNavigator';
import CandidateInterviewNavigator from './CandidateInterviewNavigator';
import CreateOpportunityNavigator from './CreateOpportunityNavigator';
import DashboardNavigator from './DashboardNavigator';
import EditCandidateNavigator from './EditCandidateNavigator';
import EditOpportunityNavigator from './EditOpportunityNavigator';
import RootNavigator from './RootNavigator';
import SettingsNavigator from './SettingsNavigator';
import IntegrationSettingsNavigator from './SettingsNavigator/IntegrationSettingsNavigator';
import ShowOpportunityNavigator from './ShowOpportunityNavigator';
import ShowRequestJobPostingNavigator from './ShowRequestJobPostingNavigator';
import StaffExternalIntegrationsNavigator from './StaffNavigator/StaffExternalIntegrationsNavigator';
import StaffNavigator from './StaffNavigator/StaffNavigator';

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="">
      <Route path=":tenant" element={<RootNavigator />}>
        <Route path="" element={<Navigate to="opportunities/" replace />} />
        <Route path="" element={<DashboardNavigator />}>
          <Route
            element={
              <PermissionChecker
                requiredPermissions={[
                  'view_all_request_job_postings',
                  'view_marked_request_job_postings',
                ]}
                operation="or"
                redirectTo=".."
              />
            }
          >
            <Route path="request-job-postings">
              <Route path="" element={<RequestJobPostings />} />
              <Route
                element={
                  <PermissionChecker
                    requiredPermissions={['create_and_edit_request_job_postings']}
                    redirectTo=".."
                  />
                }
              >
                <Route path="new" element={<CreateRequestJobPosting />} />
              </Route>
              <Route path=":id">
                <Route path="" element={<ShowRequestJobPostingNavigator />}>
                  <Route path="" element={<Navigate to="analysis/" replace />} />
                  <Route path="analysis" element={<RequestJobPostingAnalysis />} />
                  <Route path="history" element={<RequestJobPostingHistory />} />
                </Route>
                <Route
                  element={
                    <PermissionChecker
                      requiredPermissions={['create_and_edit_request_job_postings']}
                      redirectTo=".."
                    />
                  }
                >
                  <Route path="change" element={<EditRequestJobPosting />} />
                </Route>
              </Route>
            </Route>
          </Route>
          <Route path="opportunities">
            <Route path="" element={<Opportunities />} />
            <Route
              element={
                <PermissionChecker
                  requiredPermissions={['create_and_edit_vacancies']}
                  redirectTo=".."
                />
              }
            >
              <Route path="new" element={<CreateOpportunityNavigator />}>
                <Route path="" element={<CreateJobPosting />} />
              </Route>
            </Route>
            <Route path=":id">
              <Route path="" element={<ShowOpportunityNavigator />}>
                <Route path="" element={<Navigate to="analysis/" replace />} />
                <Route path="analysis" element={<ApplicantAnalysis />}>
                  <Route path="candidates">
                    <Route path=":applicant_id">
                      <Route path="" element={<ApplicantNavigator />}>
                        <Route path="" element={<Navigate to="details/" replace />} />
                        <Route path="details" element={<ApplicantDetails />} />
                        <Route path="attachments" element={<CandidateAttachment />} />
                        <Route path="tests" element={<CandidateTests />} />
                        <Route path="history" element={<ApplicantHistory />} />
                        <Route path="questionnaires" element={<CandidateQuestionnaire />} />
                        <Route path="custom-tests" element={<CandidateCustomTests />} />
                        <Route path="interviews" element={<ShowCandidateInterviews />} />
                      </Route>
                      <Route path="change" element={<EditCandidateNavigator />}>
                        <Route path="" element={<Navigate to="details/" replace />} />
                        <Route path="details" element={<EditCandidateDetailsPage />} />
                        <Route path="attachments" element={<EditCandidateAttachmentPage />} />
                        <Route path="interviews" element={<EditCandidateInterviewPage />} />
                      </Route>
                    </Route>
                  </Route>
                  <Route path="vixting_admission">
                    <Route path=":vixting_admission_id">
                      <Route path="logs" element={<VixtingAdmissionLogs />} />
                    </Route>
                  </Route>
                </Route>
                <Route path="details" element={<OpportunityDetails />} />
                <Route path="manager_dashboard" element={<ManagerDashboard />} />
              </Route>
              <Route path="interview">
                <Route path=":interview_id">
                  <Route path="" element={<CandidateInterview />}>
                    <Route path="candidate" element={<CandidateInterviewNavigator />}>
                      <Route path=":applicant_id">
                        <Route path="" element={<Navigate to="details/" replace />} />
                        <Route path="details" element={<ApplicantDetails />} />
                        <Route path="attachments" element={<CandidateAttachment />} />
                        <Route path="tests" element={<CandidateTests />} />
                        <Route path="history" element={<ApplicantHistory />} />
                        <Route path="questionnaires" element={<CandidateQuestionnaire />} />
                        <Route path="custom-tests" element={<CandidateCustomTests />} />
                        <Route path="interviews" element={<ShowCandidateInterviews />} />
                      </Route>
                    </Route>
                  </Route>
                  <Route path="opinion" element={<CandidateInterviewOpinion />} />
                </Route>
              </Route>
              <Route
                element={
                  <PermissionChecker
                    requiredPermissions={['create_and_edit_vacancies']}
                    redirectTo=".."
                  />
                }
              >
                <Route path="change" element={<EditOpportunityNavigator />}>
                  <Route path="" element={<Navigate to="general/" replace />} />
                  <Route path="general" element={<EditOpportunity.GeneralForm />} />
                  <Route path="profile" element={<EditOpportunity.ProfileForm />} />
                  <Route path="description" element={<EditOpportunity.DescriptionForm />} />
                  <Route path="stages" element={<EditOpportunity.StagesForm />} />
                  <Route path="allowedUsers" element={<EditOpportunity.AllowedUsersForm />} />
                  <Route path="publication" element={<EditOpportunity.PublicationForm />} />
                </Route>
              </Route>
            </Route>
          </Route>
          <Route
            element={
              <PermissionChecker requiredPermissions={['access_talent_pool']} redirectTo=".." />
            }
          >
            <Route path="talent-pool" element={<TalentPool />}>
              <Route path="candidates">
                <Route path=":applicant_id">
                  <Route path="" element={<ApplicantNavigator />}>
                    <Route path="" element={<Navigate to="details/" replace />} />
                    <Route path="details" element={<ApplicantDetails />} />
                    <Route path="attachments" element={<CandidateAttachment />} />
                    <Route path="tests" element={<CandidateTests />} />
                    <Route path="history" element={<ApplicantHistory />} />
                    <Route path="questionnaires" element={<CandidateQuestionnaire />} />
                    <Route path="custom-tests" element={<CandidateCustomTests />} />
                    <Route path="interviews" element={<ShowCandidateInterviews />} />
                  </Route>
                  <Route path="change" element={<EditCandidateNavigator />}>
                    <Route path="" element={<Navigate to="details/" replace />} />
                    <Route path="details" element={<EditCandidateDetailsPage />} />
                    <Route path="attachments" element={<EditCandidateAttachmentPage />} />
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>
          <Route
            element={
              <PermissionChecker
                requiredPermissions={['view_all_vacancies', 'access_reports']}
                redirectTo=".."
              />
            }
          >
            <Route path="reports" element={<Reports />} />
          </Route>

          <Route
            element={
              <PermissionChecker requiredPermissions={['access_configurations']} redirectTo=".." />
            }
          >
            <Route path="settings" element={<SettingsNavigator />}>
              <Route path="" element={<Navigate to="company-info/" replace />} />
              <Route path="company-info" element={<CompanyInfo />} />
              <Route path="emailTemplates" element={<EmailTemplates />}>
                <Route path=":template_id">
                  <Route path="change" element={<EditEmailTemplatePage />} />
                </Route>
              </Route>
              <Route path="candidate-tags" element={<CandidateTags />} />
              <Route path="integrations" element={<IntegrationSettingsNavigator />}>
                <Route path="" element={<Navigate to="publication/" replace />} />
                <Route path="publication" element={<Integrations />} />
                <Route path="vixtingAdmission" element={<VixtingAdmissionSettings />} />
              </Route>
              <Route path="candidateRegisterFields" element={<CandidateRegisterFields />}>
                <Route path="candidate-source-options" element={<CandidateSourceOptionsDrawer />} />
              </Route>
              <Route path="accessManagement" element={<AccessManagementNavigator />}>
                <Route path="" element={<Navigate to="accessProfiles/" replace />} />
                <Route path="accessProfiles" element={<AccessProfiles />}>
                  <Route path="create" element={<CreateAccessProfile />} />
                  <Route path=":access_profile_id">
                    <Route path="" element={<Navigate to="change/" replace />} />
                    <Route path="change" element={<EditAccessProfile />} />
                  </Route>
                </Route>
                <Route path="users" element={<Users />}>
                  <Route path=":user_id">
                    <Route path="" element={<Navigate to="change/" replace />} />
                    <Route path="change" element={<EditUser />} />
                  </Route>
                </Route>
              </Route>
              <Route path="requestJobPostingTemplates" element={<RequestJobPostingTemplates />}>
                <Route path=":request_job_posting_template_id">
                  <Route path="change" element={<EditRequestJobPostingTemplatePage />} />
                </Route>
              </Route>
              <Route path="interviewScriptTemplates" element={<InterviewScriptTemplates />}>
                <Route path=":interview_script_template_id">
                  <Route path="change" element={<EditInterviewScriptTemplate />} />
                </Route>
              </Route>
              <Route element={<IsStaffChecker redirectTo=".." />}>
                <Route path="cs-staff" element={<StaffNavigator />}>
                  <Route path="" element={<Navigate to="staff-users/" replace />} />
                  <Route path="users" element={<Users />}>
                    <Route path="create" element={<CreateUser />} />
                    <Route path=":user_id">
                      <Route path="" element={<Navigate to="change/" replace />} />
                      <Route path="change" element={<EditUser />} />
                    </Route>
                  </Route>
                  <Route path="staff-users" element={<StaffUsers />}>
                    <Route path="create" element={<CreateStaffUser />} />
                    <Route path=":user_id">
                      <Route path="" element={<Navigate to="change/" replace />} />
                      <Route path="change" element={<EditStaffUser />} />
                    </Route>
                  </Route>
                  <Route path="import-files" element={<ImportFiles />} />
                  <Route path="tenant-config" element={<TenantConfiguration />} />
                  <Route path="mindmatch-integration" element={<MindsightKeyPairs />}>
                    <Route path=":key_pair_id">
                      <Route path="" element={<Navigate to="change/" replace />} />
                      <Route path="change" element={<EditMindsightKeyPair />} />
                    </Route>
                  </Route>
                  <Route
                    path="external-integrations"
                    element={<StaffExternalIntegrationsNavigator />}
                  >
                    <Route path="" element={<Navigate to="api/" replace />} />
                    <Route path="api" element={<StaffExternalAPI />}>
                      <Route path="create" element={<CreateOAuthClient />} />
                    </Route>
                    <Route path="sankhya" element={<StaffSankhyaConfiguration />} />
                    <Route path="vixting" element={<StaffVixtingConfiguration />} />
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<NotFound />} />
    </Route>
  )
);

export default router;
